import React, { useState } from "react";
import {
  X,
  User,
  Eye,
  EyeOff,
  Calendar,
  Briefcase,
  CheckCircle,
  Info,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

export default function BossCreateEmployee({ visible, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    employeeName: "",
    dailySalary: "",
    monthlySalary: "",
    password: "",
    type: 1, // 1 = Employee, 2 = Manager
    typeSalary: 1, // 1 = Daily, 2 = Monthly
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClose = () => {
    setFormData({
      employeeName: "",
      dailySalary: "",
      monthlySalary: "",
      password: "",
      type: 1,
      typeSalary: 1,
    });
    setShowPassword(false);
    setErrors({});
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employeeName.trim()) {
      newErrors.employeeName = t("employeeNameRequired");
    }

    if (formData.typeSalary === 1) {
      if (!formData.dailySalary || isNaN(formData.dailySalary)) {
        newErrors.dailySalary = t("enterValidDailySalary");
      }
    } else {
      if (!formData.monthlySalary || isNaN(formData.monthlySalary)) {
        newErrors.monthlySalary = t("enterValidMonthlySalary");
      }
    }

    if (formData.type === 2) {
      if (!formData.password.trim()) {
        newErrors.password = t("passwordRequired");
      } else if (formData.password.trim().length < 4) {
        newErrors.password = t("passwordMinLength");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const userData = JSON.parse(localStorage.getItem("user") || "{}");

      const data = {
        Employee: {
          Name: formData.employeeName.trim(),
          Salary:
            formData.typeSalary === 1 ? parseFloat(formData.dailySalary) : 0,
          PassWord: formData.type === 2 ? formData.password.trim() : "",
          Type: formData.type,
          TypeSalary: formData.typeSalary,
          MonthlySalary:
            formData.typeSalary === 2 ? parseFloat(formData.monthlySalary) : 0,
          Active: 1,
        },
        UserId: userData.id,
      };

      const response = await axios.post(
        `${MyDomain}Employee/CreateEmployee`,
        data
      );

      if (response.status === 200) {
        handleClose();
        if (onSuccess) onSuccess(response.data);
        setSuccessVisible(true);
        setTimeout(() => setSuccessVisible(false), 2500);
      }
    } catch (error) {
      console.error("Error creating employee:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        t("failedToCreateEmployee");
      setErrorMsg(String(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const salaryValid =
    (formData.typeSalary === 1 &&
      formData.dailySalary &&
      !isNaN(formData.dailySalary) &&
      parseFloat(formData.dailySalary) >= 0) ||
    (formData.typeSalary === 2 &&
      formData.monthlySalary &&
      !isNaN(formData.monthlySalary) &&
      parseFloat(formData.monthlySalary) >= 0);

  const passwordValid =
    formData.type === 1 ||
    (formData.password.trim() && formData.password.trim().length >= 4);

  const isFormValid = formData.employeeName.trim() && salaryValid && passwordValid;

  if (!visible && !successVisible) return null;

  return (
    <>
      {visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle Bar (Mobile) */}
        <div className="sm:hidden w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4"></div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="text-center pt-8 pb-6 px-6">
          <div className="text-5xl mb-3">👤</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t("createNewEmployee")}
          </h2>
          <p className="text-gray-600">{t("addNewTeamMember")}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleCreateEmployee} className="px-6 pb-8 space-y-5">
          {/* Name Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("employeeName")}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">
                👨‍💼
              </span>
              <input
                type="text"
                value={formData.employeeName}
                onChange={(e) => handleChange("employeeName", e.target.value)}
                placeholder={t("enterEmployeeName")}
                maxLength={50}
                className={`w-full pl-14 pr-4 py-4 bg-gray-50 border-2 ${
                  errors.employeeName ? "border-red-500" : "border-gray-200"
                } rounded-2xl focus:outline-none focus:border-blue-500 transition`}
              />
            </div>
            {errors.employeeName && (
              <p className="mt-1 text-sm text-red-600">{errors.employeeName}</p>
            )}
          </div>

          {/* Employee Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("employeeType")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleChange("type", 1)}
                className={`relative p-4 rounded-2xl border-2 transition ${
                  formData.type === 1
                    ? "bg-blue-50 border-blue-500"
                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <User
                      className={`w-6 h-6 ${
                        formData.type === 1 ? "text-blue-600" : "text-gray-600"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-semibold ${
                      formData.type === 1 ? "text-blue-900" : "text-gray-600"
                    }`}
                  >
                    {t("employee")}
                  </span>
                </div>
                {formData.type === 1 && (
                  <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-blue-600" />
                )}
              </button>

              <button
                type="button"
                onClick={() => handleChange("type", 2)}
                className={`relative p-4 rounded-2xl border-2 transition ${
                  formData.type === 2
                    ? "bg-blue-50 border-blue-500"
                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Briefcase
                      className={`w-6 h-6 ${
                        formData.type === 2 ? "text-blue-600" : "text-gray-600"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-semibold ${
                      formData.type === 2 ? "text-blue-900" : "text-gray-600"
                    }`}
                  >
                    {t("manager")}
                  </span>
                </div>
                {formData.type === 2 && (
                  <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-blue-600" />
                )}
              </button>
            </div>
          </div>

          {/* Info Card */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 leading-relaxed">
              {formData.type === 1
                ? t("employeeTypeDesc")
                : t("managerTypeDesc")}
            </p>
          </div>

          {/* Salary Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("salaryType")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleChange("typeSalary", 1)}
                className={`relative p-4 rounded-2xl border-2 transition ${
                  formData.typeSalary === 1
                    ? "bg-blue-50 border-blue-500"
                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Calendar
                      className={`w-6 h-6 ${
                        formData.typeSalary === 1
                          ? "text-blue-600"
                          : "text-gray-600"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-semibold ${
                      formData.typeSalary === 1
                        ? "text-blue-900"
                        : "text-gray-600"
                    }`}
                  >
                    {t("daily")}
                  </span>
                </div>
                {formData.typeSalary === 1 && (
                  <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-blue-600" />
                )}
              </button>

              <button
                type="button"
                onClick={() => handleChange("typeSalary", 2)}
                className={`relative p-4 rounded-2xl border-2 transition ${
                  formData.typeSalary === 2
                    ? "bg-blue-50 border-blue-500"
                    : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Calendar
                      className={`w-6 h-6 ${
                        formData.typeSalary === 2
                          ? "text-blue-600"
                          : "text-gray-600"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-semibold ${
                      formData.typeSalary === 2
                        ? "text-blue-900"
                        : "text-gray-600"
                    }`}
                  >
                    {t("monthly")}
                  </span>
                </div>
                {formData.typeSalary === 2 && (
                  <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-blue-600" />
                )}
              </button>
            </div>
          </div>

          {/* Conditional Salary Input */}
          {formData.typeSalary === 1 ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("dailySalary")}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">
                  💰
                </span>
                <input
                  type="number"
                  value={formData.dailySalary}
                  onChange={(e) => handleChange("dailySalary", e.target.value)}
                  placeholder={t("dailySalary")}
                  step="0.01"
                  className={`w-full pl-14 pr-4 py-4 bg-gray-50 border-2 ${
                    errors.dailySalary ? "border-red-500" : "border-gray-200"
                  } rounded-2xl focus:outline-none focus:border-blue-500 transition`}
                />
              </div>
              {errors.dailySalary && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.dailySalary}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                Estimated salary — you can change and update it later.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("monthlySalary")}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">
                  💵
                </span>
                <input
                  type="number"
                  value={formData.monthlySalary}
                  onChange={(e) =>
                    handleChange("monthlySalary", e.target.value)
                  }
                  placeholder={t("monthlySalary")}
                  step="0.01"
                  className={`w-full pl-14 pr-4 py-4 bg-gray-50 border-2 ${
                    errors.monthlySalary ? "border-red-500" : "border-gray-200"
                  } rounded-2xl focus:outline-none focus:border-blue-500 transition`}
                />
              </div>
              {errors.monthlySalary && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.monthlySalary}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                Estimated salary — you can change and update it later.
              </p>
            </div>
          )}

          {/* Password Input — only for Manager */}
          {formData.type === 2 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("password")}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">
                  🔒
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder={t("enterPassword")}
                  maxLength={50}
                  className={`w-full pl-14 pr-12 py-4 bg-gray-50 border-2 ${
                    errors.password ? "border-red-500" : "border-gray-200"
                  } rounded-2xl focus:outline-none focus:border-blue-500 transition`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-lg transition"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-600" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                You can change or update it later.
              </p>
            </div>
          )}

          {/* Error Message */}
          {errorMsg && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <span className="text-red-500 text-lg">❌</span>
              <p className="text-sm text-red-700 flex-1">{errorMsg}</p>
              <button
                type="button"
                onClick={() => setErrorMsg("")}
                className="text-red-400 hover:text-red-600 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition shadow-lg ${
              isFormValid && !isLoading
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isLoading ? t("creating") : t("createEmployee")}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }

        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-toast-in { animation: toast-in 0.25s ease-out forwards; }

        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
      )}

      {successVisible && (
        <div className="fixed bottom-6 left-6 z-[60] bg-white rounded-2xl shadow-xl border border-gray-100 px-5 py-4 flex items-center gap-3 animate-toast-in">
          <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {t("employeeCreatedSuccessfully")}
          </p>
        </div>
      )}
    </>
  );
}
